// Firebase imports
import { 
  collection, 
  addDoc, 
  getDocs, 
  getDoc,
  doc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit, 
  serverTimestamp,
  Timestamp 
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { DRXError, toDRXError } from '@/lib/drx-error';
import { INotificationRepository } from './interfaces';

// Notification entity interfaces
export interface CreateNotificationData {
  userId: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error' | 'system';
  category: 'course' | 'connect' | 'payment' | 'message' | 'system' | 'achievement' | 'reminder';
  isRead: boolean;
  actionUrl?: string;
  actionText?: string;
  expiresAt?: Date;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  metadata?: Record<string, any>;
}

export interface UpdateNotificationData {
  title?: string;
  message?: string;
  isRead?: boolean;
  actionUrl?: string;
  actionText?: string;
  expiresAt?: Date;
  priority?: 'low' | 'normal' | 'high' | 'urgent';
  metadata?: Record<string, any>;
  updatedAt?: Timestamp;
}

export interface NotificationFilter {
  userId?: string;
  type?: 'info' | 'success' | 'warning' | 'error' | 'system';
  category?: 'course' | 'connect' | 'payment' | 'message' | 'system' | 'achievement' | 'reminder';
  isRead?: boolean;
  priority?: 'low' | 'normal' | 'high' | 'urgent';
  dateFrom?: Date;
  dateTo?: Date;
}

export interface GetNotificationData {
  id: string;
  userId: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error' | 'system';
  category: 'course' | 'connect' | 'payment' | 'message' | 'system' | 'achievement' | 'reminder';
  isRead: boolean;
  actionUrl?: string;
  actionText?: string;
  expiresAt?: Timestamp;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  metadata?: Record<string, any>;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export class NotificationRepository implements INotificationRepository {
  constructor(private db: any) {
    console.log('[NotificationRepository] Constructor - Injected Firebase Admin DB instance:', !!this.db);
  }

  private collectionName = 'notifications';

  private toRepositoryError(error: unknown, code: string, devMessage: string) {
    return toDRXError(error, {
      code,
      devMessage,
    });
  }

  // Create notification
  async createNotification(notificationData: CreateNotificationData): Promise<GetNotificationData> {
    try {
      const docRef = await addDoc(collection(db, this.collectionName), {
        ...notificationData,
        isRead: notificationData.isRead ?? false,
        priority: notificationData.priority ?? 'normal',
        expiresAt: notificationData.expiresAt ? Timestamp.fromDate(notificationData.expiresAt) : null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        throw new DRXError({
          code: 'NOTIFICATION_CREATE_MISSING_DOCUMENT',
          devMessage: 'Notification document was missing after create',
        });
      }

      return {
        id: docRef.id,
        ...docSnap.data(),
      } as GetNotificationData;
    } catch (error) {
      console.error('Error creating notification:', error);
      throw this.toRepositoryError(error, 'NOTIFICATION_CREATE_FAILED', 'Failed to create notification');
    }
  }

  // Get notification by ID
  async getNotificationById(notificationId: string): Promise<GetNotificationData | null> {
    try {
      const docRef = doc(db, this.collectionName, notificationId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      return {
        id: docSnap.id,
        ...docSnap.data(),
      } as GetNotificationData;
    } catch (error) {
      console.error('Error getting notification by ID:', error);
      throw this.toRepositoryError(error, 'NOTIFICATION_FETCH_FAILED', `Failed to get notification ${notificationId}`);
    }
  }

  // Get notifications with filters
  async getNotifications(filter: NotificationFilter = {}, limitCount?: number): Promise<GetNotificationData[]> {
    try {
      let q = collection(db, this.collectionName);

      // Build query with filters
      if (filter.userId) {
        q = query(q, where('userId', '==', filter.userId));
      }
      if (filter.type) {
        q = query(q, where('type', '==', filter.type));
      }
      if (filter.category) {
        q = query(q, where('category', '==', filter.category));
      }
      if (filter.isRead !== undefined) {
        q = query(q, where('isRead', '==', filter.isRead));
      }
      if (filter.priority) {
        q = query(q, where('priority', '==', filter.priority));
      }

      // Add ordering and limit
      q = query(q, orderBy('createdAt', 'desc'));
      if (limitCount) {
        q = query(q, limit(limitCount));
      }

      const querySnapshot = await getDocs(q);
      let notifications: GetNotificationData[] = [];

      querySnapshot.forEach((doc) => {
        notifications.push({
          id: doc.id,
          ...doc.data(),
        } as GetNotificationData);
      });

      // Apply date filters (since Firestore doesn't support range queries with other filters easily)
      if (filter.dateFrom) {
        notifications = notifications.filter(notification =>
          notification.createdAt.toDate() >= filter.dateFrom!
        );
      }
      if (filter.dateTo) {
        notifications = notifications.filter(notification =>
          notification.createdAt.toDate() <= filter.dateTo!
        );
      }

      return notifications;
    } catch (error) {
      console.error('Error getting notifications:', error);
      throw this.toRepositoryError(error, 'NOTIFICATIONS_FETCH_FAILED', 'Failed to get notifications');
    }
  }

  // Update notification
  async updateNotification(notificationId: string, updateData: UpdateNotificationData): Promise<GetNotificationData> {
    try {
      const docRef = doc(db, this.collectionName, notificationId);

      // Convert dates to Timestamps if provided
      const dataToUpdate: any = { ...updateData };
      if (updateData.expiresAt) {
        dataToUpdate.expiresAt = Timestamp.fromDate(updateData.expiresAt);
      }

      await updateDoc(docRef, {
        ...dataToUpdate,
        updatedAt: serverTimestamp(),
      });

      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        throw new DRXError({
          code: 'NOTIFICATION_NOT_FOUND_AFTER_UPDATE',
          devMessage: `Notification ${notificationId} not found after update`,
          userMessage: 'Notification not found.',
          level: 'warning',
        });
      }

      return {
        id: docSnap.id,
        ...docSnap.data(),
      } as GetNotificationData;
    } catch (error) {
      console.error('Error updating notification:', error);
      throw this.toRepositoryError(error, 'NOTIFICATION_UPDATE_FAILED', `Failed to update notification ${notificationId}`);
    }
  }

  // Delete notification
  async deleteNotification(notificationId: string): Promise<void> {
    try {
      const docRef = doc(db, this.collectionName, notificationId);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error deleting notification:', error);
      throw this.toRepositoryError(error, 'NOTIFICATION_DELETE_FAILED', `Failed to delete notification ${notificationId}`);
    }
  }

  // Get notifications by type
  async getNotificationsByType(type: 'info' | 'success' | 'warning' | 'error' | 'system', limitCount?: number): Promise<GetNotificationData[]> {
    try {
      const filter: NotificationFilter = { type };
      return this.getNotifications(filter, limitCount);
    } catch (error) {
      console.error('Error getting notifications by type:', error);
      throw this.toRepositoryError(error, 'NOTIFICATIONS_BY_TYPE_FETCH_FAILED', `Failed to get notifications by type ${type}`);
    }
  }

  // Get notifications by category
  async getNotificationsByCategory(category: 'course' | 'connect' | 'payment' | 'message' | 'system' | 'achievement' | 'reminder', limitCount?: number): Promise<GetNotificationData[]> {
    try {
      const filter: NotificationFilter = { category };
      return this.getNotifications(filter, limitCount);
    } catch (error) {
      console.error('Error getting notifications by category:', error);
      throw this.toRepositoryError(error, 'NOTIFICATIONS_BY_CATEGORY_FETCH_FAILED', `Failed to get notifications by category ${category}`);
    }
  }

  // Get notifications by priority
  async getNotificationsByPriority(priority: 'low' | 'normal' | 'high' | 'urgent', limitCount?: number): Promise<GetNotificationData[]> {
    try {
      const filter: NotificationFilter = { priority };
      return this.getNotifications(filter, limitCount);
    } catch (error) {
      console.error('Error getting notifications by priority:', error);
      throw this.toRepositoryError(error, 'NOTIFICATIONS_BY_PRIORITY_FETCH_FAILED', `Failed to get notifications by priority ${priority}`);
    }
  }

  // Get user notifications
  async getUserNotifications(userId: string, limitCount?: number): Promise<GetNotificationData[]> {
    try {
      const filter: NotificationFilter = { userId: userId };
      return this.getNotifications(filter, limitCount);
    } catch (error) {
      console.error('Error getting user notifications:', error);
      throw this.toRepositoryError(error, 'NOTIFICATIONS_BY_USER_FETCH_FAILED', `Failed to get notifications for user ${userId}`);
    }
  }

  // Get notifications by owner (creator/sender)
  async getNotificationsByOwner(ownerId: string, limitCount?: number): Promise<GetNotificationData[]> {
    try {
      // Notifications don't have a direct owner field, but we can check metadata or system notifications
      // For now, return empty array as notifications are typically system-generated
      console.log('Notifications by owner not directly supported - returning empty array');
      return [];
    } catch (error) {
      console.error('Error getting notifications by owner:', error);
      throw this.toRepositoryError(error, 'NOTIFICATIONS_BY_OWNER_FETCH_FAILED', `Failed to get notifications by owner ${ownerId}`);
    }
  }

  // Get all notifications (admin only) or user's notifications
  async getNotificationsForUser(userId: string, isAdmin: boolean = false, limitCount?: number): Promise<GetNotificationData[]> {
    try {
      if (isAdmin) {
        // Admin sees all notifications
        return this.getNotifications({}, limitCount);
      } else {
        // Regular user sees only their own notifications
        return this.getUserNotifications(userId, limitCount);
      }
    } catch (error) {
      console.error('Error getting notifications for user:', error);
      throw this.toRepositoryError(error, 'NOTIFICATIONS_FOR_USER_FETCH_FAILED', `Failed to get notifications for user ${userId}`);
    }
  }

  // Get all notifications (admin only)
  async getAllNotifications(limitCount?: number): Promise<GetNotificationData[]> {
    try {
      return this.getNotifications({}, limitCount);
    } catch (error) {
      console.error('Error getting all notifications:', error);
      throw this.toRepositoryError(error, 'NOTIFICATIONS_ALL_FETCH_FAILED', 'Failed to get all notifications');
    }
  }

  // Get unread notifications count
  async getUnreadNotificationsCount(userId: string): Promise<number> {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('userId', '==', userId),
        where('isRead', '==', false)
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.size;
    } catch (error) {
      console.error('Error getting unread notifications count:', error);
      throw this.toRepositoryError(error, 'NOTIFICATIONS_UNREAD_COUNT_FAILED', `Failed to get unread notification count for user ${userId}`);
    }
  }

  // Mark notification as read
  async markNotificationAsRead(notificationId: string): Promise<GetNotificationData> {
    try {
      const docRef = doc(db, this.collectionName, notificationId);
      await updateDoc(docRef, {
        isRead: true,
        read: true,
        updatedAt: serverTimestamp(),
      });

      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        throw new DRXError({
          code: 'NOTIFICATION_NOT_FOUND_AFTER_UPDATE',
          devMessage: `Notification ${notificationId} not found after update`,
          userMessage: 'Notification not found.',
          level: 'warning',
        });
      }

      return {
        id: docSnap.id,
        ...docSnap.data(),
      } as GetNotificationData;
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw this.toRepositoryError(error, 'NOTIFICATION_MARK_READ_FAILED', `Failed to mark notification ${notificationId} as read`);
    }
  }

  // Mark all notifications as read for user.
  //
  // The notifications rule requires `resource.data.userId == auth.uid`,
  // so a query filtered on `recipientId` is rejected by Firestore's
  // static rule check (same bug class as the bell-icon listener). We
  // run the `userId` branch (covers all new docs and dual-written
  // legacy docs) and try the `recipientId` branch but swallow rule
  // denials so the entire mark-all doesn't fail when a legacy
  // recipient-only doc exists.
  async markAllNotificationsAsRead(userId: string): Promise<void> {
    try {
      // Query by userId ONLY (the [userId, createdAt] index covers this) and
      // filter unread client-side. The previous read/isRead-FILTERED queries
      // needed a [userId, read] / [userId, isRead] composite index that does
      // NOT exist, so they threw FAILED_PRECONDITION and nothing got marked —
      // which is why "mark all as read" never stuck and the bell bubble kept
      // regenerating. This also catches notifications missing a read/isRead
      // field entirely (a where('read','==',false) query skips those).
      const q = query(collection(db, this.collectionName), where('userId', '==', userId));
      const snap = await getDocs(q);
      const updatePromises = snap.docs
        .filter((d) => {
          const data = d.data();
          return data.read !== true || data.isRead !== true;
        })
        .map((d) =>
          updateDoc(d.ref, {
            isRead: true,
            read: true,
            updatedAt: serverTimestamp(),
          }),
        );
      await Promise.all(updatePromises);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      throw this.toRepositoryError(error, 'NOTIFICATIONS_MARK_ALL_READ_FAILED', `Failed to mark all notifications as read for user ${userId}`);
    }
  }

  /**
   * Job / connect panel notifications: dual-writes `userId` + `recipientId` and `isRead` + `read`
   * so repository queries and legacy panel code stay compatible.
   */
  async createJobPanelNotification(input: {
    recipientId: string;
    senderId: string;
    title: string;
    message: string;
    panelType: string;
    data?: unknown;
    actionUrl?: string;
    read: boolean;
  }): Promise<string> {
    try {
      const docRef = await addDoc(collection(db, this.collectionName), {
        userId: input.recipientId,
        recipientId: input.recipientId,
        senderId: input.senderId,
        title: input.title,
        message: input.message,
        type: 'info',
        category: 'system',
        isRead: input.read,
        read: input.read,
        priority: 'normal',
        metadata: {
          panelType: input.panelType,
          payload: input.data,
        },
        ...(input.actionUrl ? { actionUrl: input.actionUrl } : {}),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return docRef.id;
    } catch (error) {
      throw this.toRepositoryError(
        error,
        'PANEL_NOTIFICATION_CREATE_FAILED',
        'Failed to create panel notification',
      );
    }
  }

  /** The reader's notifications (by `userId`) for the panel UI. */
  async getPanelNotificationsForUser(
    userId: string,
    unreadOnly = false,
    limitCount = 50,
  ): Promise<Array<Record<string, unknown> & { id: string }>> {
    const fetchBranch = async (
      field: 'userId',
      unreadField: 'isRead',
    ): Promise<Array<Record<string, unknown> & { id: string }>> => {
      try {
        let q = query(collection(db, this.collectionName), where(field, '==', userId));
        if (unreadOnly) {
          q = query(q, where(unreadField, '==', false));
        }
        q = query(q, orderBy('createdAt', 'desc'), limit(limitCount));
        const snap = await getDocs(q);
        return snap.docs.map((d) => ({ id: d.id, ...d.data() })) as Array<
          Record<string, unknown> & { id: string }
        >;
      } catch {
        let q = query(collection(db, this.collectionName), where(field, '==', userId));
        if (unreadOnly) {
          q = query(q, where(unreadField, '==', false));
        }
        const snap = await getDocs(q);
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() })) as Array<
          Record<string, unknown> & { id: string }
        >;
        return rows
          .sort((a, b) => {
            const sa =
              a.createdAt &&
              typeof a.createdAt === 'object' &&
              'seconds' in (a.createdAt as object)
                ? (a.createdAt as { seconds: number }).seconds
                : 0;
            const sb =
              b.createdAt &&
              typeof b.createdAt === 'object' &&
              'seconds' in (b.createdAt as object)
                ? (b.createdAt as { seconds: number }).seconds
                : 0;
            return sb - sa;
          })
          .slice(0, limitCount);
      }
    };

    // userId only. The rules allow a read only when userId is the reader, so
    // a recipientId query is always rejected -- it made this whole fetch
    // (the bell's fallback when the live feed is slow) fail for everyone.
    // New and dual-written docs carry userId; a recipientId-only doc was
    // unreadable under the rules anyway.
    const byUserId = await fetchBranch('userId', 'isRead');
    const merged = new Map<string, Record<string, unknown> & { id: string }>();
    for (const r of byUserId) {
      merged.set(r.id, r);
    }
    return Array.from(merged.values())
      .sort((a, b) => {
        const sa =
          a.createdAt &&
          typeof a.createdAt === 'object' &&
          'seconds' in (a.createdAt as object)
            ? (a.createdAt as { seconds: number }).seconds
            : 0;
        const sb =
          b.createdAt &&
          typeof b.createdAt === 'object' &&
          'seconds' in (b.createdAt as object)
            ? (b.createdAt as { seconds: number }).seconds
            : 0;
        return sb - sa;
      })
      .slice(0, limitCount);
  }

  // Create system notification for all users
  async createSystemNotification(title: string, message: string, type: 'info' | 'warning' | 'error' = 'info'): Promise<GetNotificationData> {
    try {
      // This would typically get all users and create notifications for each
      // For now, we'll create a broadcast-style notification
      const notificationData: CreateNotificationData = {
        userId: 'system', // Special user ID for system notifications
        title,
        message,
        type,
        category: 'system',
        isRead: false,
        priority: 'normal',
      };

      return this.createNotification(notificationData);
    } catch (error) {
      console.error('Error creating system notification:', error);
      throw this.toRepositoryError(error, 'NOTIFICATION_SYSTEM_CREATE_FAILED', 'Failed to create system notification');
    }
  }

  // Create course notification
  async createCourseNotification(userId: string, courseId: string, title: string, message: string): Promise<GetNotificationData> {
    try {
      const notificationData: CreateNotificationData = {
        userId: userId,
        title,
        message,
        type: 'info',
        category: 'course',
        isRead: false,
        priority: 'normal',
        metadata: { courseId: courseId },
      };

      return this.createNotification(notificationData);
    } catch (error) {
      console.error('Error creating course notification:', error);
      throw this.toRepositoryError(error, 'NOTIFICATION_COURSE_CREATE_FAILED', `Failed to create course notification for user ${userId}`);
    }
  }

  async createConnectNotification(userId: string, requestId: string, title: string, message: string, type: 'info' | 'success' | 'warning' = 'info'): Promise<GetNotificationData> {
    try {
      const notificationData: CreateNotificationData = {
        userId: userId,
        title,
        message,
        type,
        category: 'connect',
        isRead: false,
        priority: 'normal',
        metadata: { requestId },
      };

      return this.createNotification(notificationData);
    } catch (error) {
      console.error('Error creating connect notification:', error);
      throw this.toRepositoryError(error, 'NOTIFICATION_CONNECT_CREATE_FAILED', `Failed to create connect notification for user ${userId}`);
    }
  }

  // Create payment notification
  async createPaymentNotification(userId: string, paymentId: string, title: string, message: string, type: 'success' | 'warning' | 'error' = 'success'): Promise<GetNotificationData> {
    try {
      const notificationData: CreateNotificationData = {
        userId: userId,
        title,
        message,
        type,
        category: 'payment',
        isRead: false,
        priority: 'high',
        metadata: { paymentId: paymentId },
      };

      return this.createNotification(notificationData);
    } catch (error) {
      console.error('Error creating payment notification:', error);
      throw this.toRepositoryError(error, 'NOTIFICATION_PAYMENT_CREATE_FAILED', `Failed to create payment notification for user ${userId}`);
    }
  }

  // Create achievement notification
  async createAchievementNotification(userId: string, achievement: string, title: string, message: string): Promise<GetNotificationData> {
    try {
      const notificationData: CreateNotificationData = {
        userId: userId,
        title,
        message,
        type: 'success',
        category: 'achievement',
        isRead: false,
        priority: 'normal',
        metadata: { achievement },
      };

      return this.createNotification(notificationData);
    } catch (error) {
      console.error('Error creating achievement notification:', error);
      throw this.toRepositoryError(error, 'NOTIFICATION_ACHIEVEMENT_CREATE_FAILED', `Failed to create achievement notification for user ${userId}`);
    }
  }

  // Get expired notifications
  async getExpiredNotifications(): Promise<GetNotificationData[]> {
    try {
      const now = Timestamp.fromDate(new Date());
      const q = query(
        collection(db, this.collectionName),
        where('expiresAt', '<=', now),
        orderBy('expiresAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const notifications: GetNotificationData[] = [];

      querySnapshot.forEach((doc) => {
        notifications.push({
          id: doc.id,
          ...doc.data(),
        } as GetNotificationData);
      });

      return notifications;
    } catch (error) {
      console.error('Error getting expired notifications:', error);
      throw this.toRepositoryError(error, 'NOTIFICATIONS_EXPIRED_FETCH_FAILED', 'Failed to get expired notifications');
    }
  }

  // Clean up expired notifications
  async cleanupExpiredNotifications(): Promise<number> {
    try {
      const expiredNotifications = await this.getExpiredNotifications();

      const deletePromises = expiredNotifications.map(notification =>
        deleteDoc(doc(db, this.collectionName, notification.id))
      );

      await Promise.all(deletePromises);
      return expiredNotifications.length;
    } catch (error) {
      console.error('Error cleaning up expired notifications:', error);
      throw this.toRepositoryError(error, 'NOTIFICATIONS_EXPIRED_CLEANUP_FAILED', 'Failed to clean up expired notifications');
    }
  }
}
